import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from './use-api-client.js';

/** The operator's meetings of the last 14 days and the next 7 (ADR-037); read again every 5 minutes while open */
export function useMeetings(serverId: string) {
  const api = useApiClient();
  return useQuery({ queryKey: ['meetings', serverId], queryFn: () => api.meetings.list(serverId), refetchInterval: 5 * 60_000, staleTime: 60_000 });
}

/** One meeting's notes, read from Docs by the API when asked */
export function useMeetingNotes(serverId: string, eventId: string, docId: string, enabled: boolean) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['meeting-notes', serverId, eventId, docId], queryFn: () => api.meetings.notes(serverId, eventId, docId).then((r) => r.notes),
    enabled, staleTime: 5 * 60_000,
  });
}

/** Start a session: a main session from the meeting and its notes, the same one when started again (ADR-037 step 2) */
export function useStartMeetingSession(serverId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId }: { eventId: string }) => api.meetings.startSession(serverId, eventId),
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['meetings', serverId] }); },
  });
}

/** Apply the plan's lines kept, or dismiss it (ADR-037 step 3) */
export function useMeetingPlanActions(serverId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  const settle = () => { void qc.invalidateQueries({ queryKey: ['meetings', serverId] }); };
  return {
    apply: useMutation({ mutationFn: ({ eventId, keys }: { eventId: string; keys: string[] }) => api.meetings.applyPlan(serverId, eventId, keys), onSettled: settle }),
    dismiss: useMutation({ mutationFn: ({ eventId }: { eventId: string }) => api.meetings.dismissPlan(serverId, eventId), onSettled: settle }),
  };
}
